import { Icon, Image } from '@acme/assets';
import { CopyableText, formatNumber } from '@acme/foundation';
import type { DealParticipantRow } from '@widgets/providersTable';
import {
  Dropdown,
  DropdownItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from 'flowbite-react';
import { twMerge } from 'tailwind-merge';

interface ApplicationProvidersDealTableProps {
  rows: DealParticipantRow[];
  className?: string;
  onChangeAmount?: (providerId: string) => void;
  onRemove?: (providerId: string) => void;
}

export const ApplicationProvidersDealTable = ({
  rows,
  className,
  onChangeAmount,
  onRemove,
}: ApplicationProvidersDealTableProps) => {
  const serverRows = rows.filter(
    (r): r is DealParticipantRow & { source: 'server' } => r.source === 'server'
  );
  const hasAnyWalletAddress = serverRows.some(
    (r) => r.data.walletAddress != null && r.data.walletAddress.trim() !== ''
  );

  if (!rows.length) {
    return (
      <div
        className={twMerge(
          className,
          'flex flex-col items-center justify-center py-12'
        )}
      >
        <div className="flex flex-col gap-4 items-center max-w-[448px]">
          <div className="w-40 overflow-hidden shrink-0">
            <Image
              name="select-provider"
              alt="Выберите поставщика"
              className="w-full h-full object-contain"
              width="100%"
              height="100%"
            />
          </div>
          <p className="text-center text-base text-gray-900">
            Наберите нужную вам сумму из средств
            <br />
            доступных поставщиков
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={twMerge('w-full', className)}>
      <h3 className="text-lg font-semibold text-gray-900 my-6">
        Участники сделки
      </h3>
      <Table className="w-full -mx-6">
        <TableHead>
          <TableRow>
            <TableHeadCell>Заказ</TableHeadCell>
            <TableHeadCell>Название организации</TableHeadCell>
            <TableHeadCell>Курс обмена</TableHeadCell>
            <TableHeadCell>Объем валюты</TableHeadCell>
            <TableHeadCell>Сумма в рублях</TableHeadCell>
            {hasAnyWalletAddress && (
              <TableHeadCell>Адрес криптокошелька</TableHeadCell>
            )}
            <TableHeadCell>Действия</TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            if (row.source === 'server') {
              const p = row.data;
              return (
                <TableRow key={p.id}>
                  <TableCell>{p.epoch?.name}</TableCell>
                  <TableCell>{p.organizationName}</TableCell>
                  <TableCell>{formatNumber(p.price)}</TableCell>
                  <TableCell>{formatNumber(p.amount)}</TableCell>
                  <TableCell>{formatNumber(p.sum)}</TableCell>
                  {hasAnyWalletAddress && (
                    <TableCell>
                      <CopyableText value={p.walletAddress} />
                    </TableCell>
                  )}
                  <TableCell />
                </TableRow>
              );
            }
            return (
              <TableRow key={row.id}>
                <TableCell>—</TableCell>
                <TableCell>{row.organizationName}</TableCell>
                <TableCell>{formatNumber(row.price)}</TableCell>
                <TableCell>{formatNumber(row.amount)}</TableCell>
                <TableCell>{formatNumber(row.sum)}</TableCell>
                {hasAnyWalletAddress && <TableCell>—</TableCell>}
                <TableCell>
                  <Dropdown
                    label=""
                    dismissOnClick
                    renderTrigger={() => (
                      <button
                        type="button"
                        className="p-1 rounded hover:bg-gray-100"
                        aria-label="Действия"
                      >
                        <Icon
                          name="dots-horizontal-outline"
                          size={20}
                          className="text-gray-600"
                        />
                      </button>
                    )}
                  >
                    <DropdownItem onClick={() => onChangeAmount?.(row.id)}>
                      <span className="flex items-center gap-2">
                        <Icon name="file-lines-outline" size={16} />
                        Изменить сумму
                      </span>
                    </DropdownItem>
                    <DropdownItem
                      className="text-red-600 focus:text-red-600"
                      onClick={() => onRemove?.(row.id)}
                    >
                      <span className="flex items-center gap-2">
                        <Icon
                          name="x-outline"
                          size={16}
                          className="text-red-600"
                        />
                        Удалить из заказа
                      </span>
                    </DropdownItem>
                  </Dropdown>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
